import {
  AttachIcon,
  CheckIcon,
  CloseIcon,
  FileIcon,
  SpinnerIcon,
  WarningIcon,
} from "@/components/icons";
import { Button } from "@/components/ui/button";
import { cn } from "cn";
import { useId, useRef } from "react";

import { keysLabel } from "../work/digitacion/keymap.ts";
import { formatBytes, ALFRED_TEXTS as TEXTS } from "./alfred.ts";
import { ALFRED_ACCEPT } from "./attachment-rules.ts";
import type { PendingFile, PendingFiles } from "./use-attachments.ts";

/** Pegar: Ctrl+V en un PC, ⌘V en Mac. */
const PASTE_KEYS = ["Ctrl", "V"];

function StateOf({ item }: { item: PendingFile }) {
  switch (item.state) {
    case "uploading":
      return (
        <span className="inline-flex items-center gap-1 text-muted-foreground">
          <SpinnerIcon className="size-3 animate-spin" aria-hidden />
          {TEXTS.uploading}
        </span>
      );
    case "uploaded":
      return (
        <span className="inline-flex items-center gap-1 text-success">
          <CheckIcon className="size-3" aria-hidden />
          {TEXTS.uploaded}
        </span>
      );
    case "failed":
      return (
        <span className="inline-flex items-center gap-1 text-destructive">
          <WarningIcon className="size-3" aria-hidden />
          {TEXTS.uploadFailed}
          {item.error === null ? "" : `: ${item.error}`}
        </span>
      );
    default:
      return null;
  }
}

/**
 * Los adjuntos de un formulario antes de enviarlo: «Adjuntar» abre el
 * selector (varios a la vez), y pegar con Ctrl+V en cualquier campo del
 * formulario los agrega también (`onPaste` del formulario, no de aquí).
 * Cada uno con su miniatura si es imagen, su peso, cómo va su subida y
 * «Quitar».
 */
export function AttachmentPicker({
  files,
  disabled = false,
}: {
  files: PendingFiles;
  disabled?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const hint = useId();

  return (
    <div className="grid gap-2">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={disabled}
          aria-describedby={hint}
          onClick={() => {
            input.current?.click();
          }}
        >
          <AttachIcon aria-hidden />
          {TEXTS.attach}
        </Button>
        <span id={hint} className="text-xs text-muted-foreground">
          {TEXTS.attachHint(keysLabel(PASTE_KEYS))}
        </span>
        <input
          ref={input}
          type="file"
          multiple
          accept={ALFRED_ACCEPT}
          hidden
          tabIndex={-1}
          aria-label={TEXTS.attach}
          onChange={(event) => {
            files.add(Array.from(event.target.files ?? []));
            /* Vacío, para que elegir otra vez el mismo archivo también
               avise. */
            event.target.value = "";
          }}
        />
      </div>

      {files.rejected.length > 0 && (
        <div role="alert" className="grid gap-0.5 text-sm text-destructive">
          {files.rejected.map((reason, index) => (
            // Se dicen en el orden en que llegaron, y no se reordenan.
            <p key={`${String(index)}-${reason}`}>{reason}</p>
          ))}
        </div>
      )}

      {files.items.length > 0 && (
        <ul aria-label={TEXTS.attachments} className="grid gap-1.5">
          {files.items.map((item) => (
            <li
              key={item.key}
              className={cn(
                "flex items-center gap-2 rounded-md border p-1.5 text-sm",
                item.state === "failed" && "border-destructive",
              )}
            >
              {item.preview === null ? (
                <span className="grid size-10 shrink-0 place-items-center rounded bg-muted">
                  <FileIcon className="text-muted-foreground" aria-hidden />
                </span>
              ) : (
                <img
                  src={item.preview}
                  alt=""
                  className="size-10 shrink-0 rounded object-cover"
                />
              )}
              <span className="grid min-w-0 flex-1">
                <span className="truncate" title={item.file.name}>
                  {item.file.name}
                </span>
                <span className="text-xs text-muted-foreground">
                  {formatBytes(item.file.size)} <StateOf item={item} />
                </span>
              </span>
              {item.state !== "uploaded" && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={TEXTS.remove(item.file.name)}
                  title={TEXTS.remove(item.file.name)}
                  disabled={item.state === "uploading"}
                  onClick={() => {
                    files.remove(item.key);
                  }}
                >
                  <CloseIcon aria-hidden />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
